import { useState } from "react";
import { supabase } from "../lib/supabaseClient";

interface SuggestionToConvert {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
}

interface Props {
  tripId: string;
  userId: string;
  suggestion: SuggestionToConvert;
  tripStartDate: string;
  tripEndDate: string;
  onClose: (converted?: boolean) => void;
}

type SchedulingMode = "none" | "date" | "datetime";

const CATEGORY_MAP: Record<string, string> = {
  Aktivität: "Aktivitäten",
  Verpflegung: "Verpflegung",
  Organisation: "Sonstiges",
  Sonstiges: "Sonstiges",
};

const PROGRAM_CATEGORIES = [
  "Reisen",
  "Übernachtung",
  "Verpflegung",
  "Aktivitäten",
  "Workshops",
  "Sonstiges",
];

export default function ConvertSuggestionModal({
  tripId,
  userId,
  suggestion,
  tripStartDate,
  tripEndDate,
  onClose,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState(suggestion.title);
  const [description, setDescription] = useState(suggestion.description ?? "");
  const [category, setCategory] = useState(
    CATEGORY_MAP[suggestion.category ?? ""] ?? "Sonstiges"
  );
  const [mode, setMode] = useState<SchedulingMode>("none");
  const [scheduledDate, setScheduledDate] = useState(tripStartDate);
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [location, setLocation] = useState("");

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Ein Titel ist erforderlich.");
      return;
    }
    if (mode === "datetime" && startTime >= endTime) {
      setError("Die Endzeit muss nach der Startzeit liegen.");
      return;
    }

    setSaving(true);
    setError("");

    const payload: Record<string, unknown> = {
      trip_id: tripId,
      creator_id: userId,
      title: title.trim(),
      description: description.trim() || null,
      category,
      from_suggestion_id: suggestion.id,
      location: location.trim() || null,
      scheduled_date: null,
      start_time: null,
      end_time: null,
    };

    if (mode === "date" || mode === "datetime") {
      payload.scheduled_date = scheduledDate;
    }
    if (mode === "datetime") {
      payload.start_time = new Date(`${scheduledDate}T${startTime}`).toISOString();
      payload.end_time = new Date(`${scheduledDate}T${endTime}`).toISOString();
    }

    const { data: newItem, error: insertError } = await supabase
      .from("trip_program_items")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .insert(payload as any)
      .select("id")
      .single();

    if (insertError || !newItem) {
      setError("Fehler beim Erstellen des Programmpunkts.");
      setSaving(false);
      return;
    }

    const { error: updateError } = await supabase
      .from("trip_suggestions")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ accepted_at: new Date().toISOString(), program_item_id: newItem.id } as any)
      .eq("id", suggestion.id);

    if (updateError) {
      setError("Programmpunkt erstellt, aber Vorschlag konnte nicht markiert werden.");
      setSaving(false);
      return;
    }

    onClose(true);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => onClose()}
          className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
          aria-label="Modal schließen"
        >
          &times;
        </button>

        <h2 className="text-sm font-semibold text-slate-100 mb-1">
          ✓ Vorschlag als Programmpunkt übernehmen
        </h2>
        <p className="text-xs text-slate-500 mb-5">
          Passe die Details an und wähle ob bereits ein Termin feststeht.
        </p>

        {/* Titel */}
        <label className="block mb-1 text-xs text-slate-400 font-medium">Titel</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
        />

        {/* Beschreibung */}
        <label className="block mb-1 text-xs text-slate-400 font-medium">
          Beschreibung <span className="text-slate-600">(optional)</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600 resize-none"
          rows={3}
          placeholder="Beschreibung (optional)"
        />

        {/* Kategorie */}
        <label className="block mb-1 text-xs text-slate-400 font-medium">Kategorie</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-200 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-sky-500/50"
        >
          {PROGRAM_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        {/* Zeitplan */}
        <label className="block mb-2 text-xs text-slate-400 font-medium">Terminplanung</label>
        <div className="space-y-2 mb-4">
          {(
            [
              { value: "none", label: "Kein Datum – noch offen" },
              { value: "date", label: "Nur Datum – Uhrzeit noch unklar" },
              { value: "datetime", label: "Datum und Uhrzeit festlegen" },
            ] as { value: SchedulingMode; label: string }[]
          ).map(({ value, label }) => (
            <label
              key={value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  mode === value
                    ? "border-sky-400 bg-sky-400/20"
                    : "border-[#2a3347] group-hover:border-slate-500"
                }`}
              >
                {mode === value && (
                  <div className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                )}
              </div>
              <input
                type="radio"
                className="hidden"
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              <span className="text-sm text-slate-300">{label}</span>
            </label>
          ))}
        </div>

        {/* Datum-Picker (mode: date oder datetime) */}
        {(mode === "date" || mode === "datetime") && (
          <>
            <label className="block mb-1 text-xs text-slate-400 font-medium">Datum</label>
            <input
              type="date"
              value={scheduledDate}
              min={tripStartDate}
              max={tripEndDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
            />
          </>
        )}

        {/* Zeit-Picker (nur mode: datetime) */}
        {mode === "datetime" && (
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <label className="block mb-1 text-xs text-slate-400 font-medium">Von</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
              />
            </div>
            <div className="flex-1">
              <label className="block mb-1 text-xs text-slate-400 font-medium">Bis</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
              />
            </div>
          </div>
        )}

        {/* Ort (nur wenn Datum gesetzt) */}
        {(mode === "date" || mode === "datetime") && (
          <>
            <label className="block mb-1 text-xs text-slate-400 font-medium">
              Ort <span className="text-slate-600">(optional)</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="z.B. Zürich Hauptbahnhof"
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
            />
          </>
        )}

        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={() => onClose()}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="text-xs px-4 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-50"
          >
            {saving ? "Wird übernommen…" : "Als Programmpunkt übernehmen"}
          </button>
        </div>
      </div>
    </div>
  );
}
